import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, Float } from '@react-three/drei';
import * as THREE from 'three';

const SECTORS = [
  { key: 'asteroid_belt', name: 'Asteroid Belt', pos: [4, 1.5, -2], color: '#f59e0b', glow: '#d97706' },
  { key: 'deep_core', name: 'Deep Core', pos: [-4, -1.5, -3], color: '#ef4444', glow: '#dc2626' },
  { key: 'nebula', name: 'Nebula Rim', pos: [0, 4, -5], color: '#a855f7', glow: '#7c3aed' },
  { key: 'frontier', name: 'Frontier', pos: [-5, 2.5, -1], color: '#22c55e', glow: '#16a34a' },
  { key: 'trade_route', name: 'Trade Route', pos: [5, -2.5, -4], color: '#3b82f6', glow: '#2563eb' },
];

function SectorNode({ sector, onSelect, isSelected }) {
  const ref = useRef();
  const ringRef = useRef();
  const [hovered, setHovered] = useState(false);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.scale.setScalar(hovered || isSelected ? 1.3 : 1.0);
    }
    if (ringRef.current) {
      ringRef.current.rotation.z += delta * 0.5;
    }
  });

  return (
    <group position={sector.pos}>
      <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.3}>
        <mesh
          ref={ref}
          onPointerOver={() => { setHovered(true); document.body.style.cursor = 'pointer'; }}
          onPointerOut={() => { setHovered(false); document.body.style.cursor = 'default'; }}
          onClick={() => onSelect && onSelect(sector)}
        >
          <sphereGeometry args={[1.4, 32, 32]} />
          <meshStandardMaterial
            color={sector.color}
            emissive={sector.glow}
            emissiveIntensity={hovered || isSelected ? 1.0 : 0.5}
            metalness={0.7}
            roughness={0.3}
          />
        </mesh>
        {/* Orbit ring */}
        <mesh ref={ringRef} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.9, 0.04, 8, 64]} />
          <meshBasicMaterial color={sector.color} transparent opacity={hovered ? 0.7 : 0.3} />
        </mesh>
        {/* Label */}
        <Text
          position={[0, 2.2, 0]}
          fontSize={0.35}
          color={hovered || isSelected ? '#ffffff' : sector.color}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {sector.name}
        </Text>
      </Float>
    </group>
  );
}

function ConnectionLines() {
  const geometry = useMemo(() => {
    const points = SECTORS.map(s => new THREE.Vector3(...s.pos));
    // Connect in a network pattern
    const linePoints = [];
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        if (points[i].distanceTo(points[j]) < 8) {
          linePoints.push(points[i], points[j]);
        }
      }
    }
    return new THREE.BufferGeometry().setFromPoints(linePoints);
  }, []);

  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial color="#1e3a5f" transparent opacity={0.3} />
    </lineSegments>
  );
}

function GalaxyDust() {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(500 * 3);
    for (let i = 0; i < 500; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 30;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 20;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 30;
    }
    return arr;
  }, []);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * 0.01;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={500} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        color="#4a90d9"
        transparent
        opacity={0.4}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

export default function GalaxyMap({ onSectorSelect, selectedSector }) {
  return (
    <div className="galaxy-map-container" style={{ height: '400px' }}>
      <Canvas camera={{ position: [0, 2, 14], fov: 55 }} gl={{ antialias: true, alpha: true }}>
        <color attach="background" args={['#050a15']} />
        <ambientLight intensity={0.15} />
        <pointLight position={[0, 5, 5]} intensity={0.8} color="#6fa8ff" />
        <pointLight position={[-5, -3, 3]} intensity={0.4} color="#a855f7" />
        <GalaxyDust />
        <ConnectionLines />
        {SECTORS.map(s => (
          <SectorNode
            key={s.key}
            sector={s}
            onSelect={onSectorSelect}
            isSelected={selectedSector?.key === s.key}
          />
        ))}
      </Canvas>
    </div>
  );
}

export { SECTORS };
